import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { describePasswordPolicyGaps } from '../../utils/passwordPolicy';
import './AuthModal.css';

interface AuthModalProps {
  mode: 'login' | 'signup';
  onClose: () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, firstName?: string, lastName?: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendCode: (email: string) => Promise<void>;
  pendingVerificationEmail?: string | null;
  pendingVerificationMode?: 'login' | 'signup' | null;
  infoMessage?: string | null;
}

const AuthModal: React.FC<AuthModalProps> = ({
  mode: initialMode = 'login',
  onClose,
  signIn,
  signUp,
  confirmSignUp,
  resendCode,
  pendingVerificationEmail,
  pendingVerificationMode,
  infoMessage
}) => {
  const [mode, setMode] = useState<'login' | 'signup' | 'verify'>(initialMode);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    confirmPassword: '',
    verificationCode: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showVerificationInSignup, setShowVerificationInSignup] = useState(false);
  const [showVerificationInLogin, setShowVerificationInLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Block background scrolling when modal is open using CSS
  useEffect(() => {
    // Add CSS class to body to prevent scrolling
    document.body.classList.add('modal-open');
    
    return () => {
      // Remove CSS class when modal closes
      document.body.classList.remove('modal-open');
    };
  }, []);

  // Sync internal mode state with prop changes (e.g., when switching from login to signup)
  useEffect(() => {
    if (initialMode !== mode) {
      console.log(`🔄 Switching modal mode from ${mode} to ${initialMode}`);
      setMode(initialMode);
      // Clear errors when switching modes
      setErrors({});
    }
  }, [initialMode]);

  // Handle pending verification email
  useEffect(() => {
    if (pendingVerificationEmail && pendingVerificationMode) {
      console.log(`📧 Showing verification field in ${pendingVerificationMode} mode for:`, pendingVerificationEmail);
      setFormData(prev => ({ ...prev, email: pendingVerificationEmail }));
      
      if (pendingVerificationMode === 'signup' && mode === 'signup') {
        setShowVerificationInSignup(true);
        setShowVerificationInLogin(false);
      } else if (pendingVerificationMode === 'login' && mode === 'login') {
        setShowVerificationInLogin(true);
        setShowVerificationInSignup(false);
      } else {
        setShowVerificationInSignup(false);
        setShowVerificationInLogin(false);
      }
    } else {
      setShowVerificationInSignup(false);
      setShowVerificationInLogin(false);
    }
  }, [pendingVerificationEmail, pendingVerificationMode, mode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      // The pool is email-only (username_attributes = ["email"]), so a bare
      // username can never sign up or sign in.
      newErrors.email = 'Email format is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (mode === 'signup') {
      // Only on signup: an existing account may predate the current policy, and
      // rejecting a correct password at sign-in would lock the user out.
      const policyError = describePasswordPolicyGaps(formData.password);
      if (policyError) {
        newErrors.password = policyError;
      }
    }

    if (mode === 'signup') {
      if (!formData.firstName.trim()) {
        newErrors.firstName = 'First name is required';
      }
      if (!formData.lastName.trim()) {
        newErrors.lastName = 'Last name is required';
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    if (mode === 'verify' || 
        (mode === 'signup' && showVerificationInSignup) || 
        (mode === 'login' && showVerificationInLogin)) {
      if (!formData.verificationCode.trim()) {
        newErrors.verificationCode = 'Verification code is required';
      } else if (formData.verificationCode.length !== 6) {
        newErrors.verificationCode = 'Verification code must be 6 digits';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      if (mode === 'login' && !showVerificationInLogin && signIn) {
        await signIn(formData.email, formData.password);
        // Only close modal if login succeeded (no error thrown)
        // If error was caught and handled (e.g., switching to signup), modal stays open
        onClose();
      } else if (mode === 'login' && showVerificationInLogin && confirmSignUp) {
        // Handle verification code submission in login form
        await confirmSignUp(formData.email, formData.verificationCode);
        onClose();
      } else if (mode === 'signup' && !showVerificationInSignup && signUp) {
        await signUp(formData.email, formData.password, formData.firstName, formData.lastName);
        // Don't close modal, switch to verification mode
        setMode('verify');
        setErrors({});
      } else if (mode === 'signup' && showVerificationInSignup && confirmSignUp) {
        // Handle verification code submission in signup form
        await confirmSignUp(formData.email, formData.verificationCode);
        onClose();
      } else if (mode === 'verify' && confirmSignUp) {
        await confirmSignUp(formData.email, formData.verificationCode);
        onClose();
      }
    } catch (error) {
      console.error('Auth error:', error);
      
      // Handle specific authentication errors with user-friendly messages
      const errorMessage = error instanceof Error ? error.message : 'Authentication failed';

      // Special case: If error is SWITCH_TO_SIGNUP, modal is switching modes - don't show error or close
      if (errorMessage === 'SWITCH_TO_SIGNUP') {
        // Modal mode has been changed by parent component, just clear loading state
        return;
      }

      // The AWS SDK carries the Cognito exception code on error.name and a
      // message whose wording it does not guarantee, so match against both —
      // on message alone, InvalidPasswordException ("Password did not conform
      // with policy: ...") fell through to the generic text below.
      const errorName = error instanceof Error ? error.name : '';
      const errorKey = `${errorName} ${errorMessage}`;

      let displayMessage = 'Authentication failed. Please try again.';

      if (errorKey.includes('User already exists') || errorKey.includes('UsernameExistsException')) {
        displayMessage = 'An account with this email already exists. Please sign in instead or check your email for verification.';
      } else if (errorKey.includes('User is not confirmed') || errorKey.includes('UserNotConfirmedException')) {
        displayMessage = 'Please check your email and verify your account before signing in.';
      } else if (errorKey.includes('Incorrect username or password') || errorKey.includes('NotAuthorizedException')) {
        displayMessage = 'Incorrect email or password. Please try again.';
      } else if (errorKey.includes('User does not exist') || errorKey.includes('UserNotFoundException')) {
        displayMessage = 'No account found with this email. Please sign up first.';
      } else if (errorKey.includes('Invalid verification code') || errorKey.includes('CodeMismatchException')) {
        displayMessage = 'Invalid verification code. Please check your email and try again.';
      } else if (errorKey.includes('Verification code expired') || errorKey.includes('ExpiredCodeException')) {
        displayMessage = 'Verification code has expired. Please request a new code.';
      } else if (errorKey.includes('Too many failed attempts') || errorKey.includes('TooManyFailedAttemptsException')) {
        displayMessage = 'Too many failed attempts. Please wait a moment before trying again.';
      } else if (errorKey.includes('LimitExceededException') || errorKey.includes('TooManyRequestsException')) {
        displayMessage = 'Too many attempts. Please wait a few minutes and try again.';
      } else if (errorKey.includes('CodeDeliveryFailureException')) {
        displayMessage = 'We could not send the verification email. Please check the address and try again.';
      } else if (errorKey.includes('Password does not meet requirements') || errorKey.includes('InvalidPasswordException')) {
        displayMessage = 'Password must be at least 8 characters and include uppercase, lowercase, numbers, and special characters.';
      } else if (errorKey.includes('Invalid email format') || errorKey.includes('InvalidParameterException')) {
        displayMessage = 'Please enter a valid email address.';
      } else if (errorKey.includes('Network error') || errorKey.includes('NetworkingError')) {
        displayMessage = 'Network error. Please check your connection and try again.';
      } else if (errorKey.includes('Service unavailable') || errorKey.includes('ServiceException')) {
        displayMessage = 'Service temporarily unavailable. Please try again in a moment.';
      }
      
      setErrors({ general: displayMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = () => {
    if (mode === 'verify') {
      // From verify mode, go back to signup
      setMode('signup');
    } else {
      setMode(mode === 'login' ? 'signup' : 'login');
    }
    setErrors({});
    setFormData({
      email: formData.email, // Keep email when switching
      password: '',
      firstName: '',
      lastName: '',
      confirmPassword: '',
      verificationCode: ''
    });
    // Reset password visibility
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  // Google is the only federated provider we plan to support (T3.5/REQ-146).
  const handleSocialAuth = async (provider: 'google') => {
    // Social auth not implemented yet
    console.log(`${provider} auth clicked - not implemented`);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Modal is always rendered when component is mounted

  const modalContent = (
    <div className="auth-modal-overlay" onClick={handleOverlayClick}>
      <div className="auth-modal">
        <div className="auth-modal-header">
          <h2 className="auth-modal-title">
            {mode === 'login' && showVerificationInLogin ? 'Verify Your Email' :
             mode === 'login' ? 'Welcome Back' : 
             mode === 'signup' && showVerificationInSignup && pendingVerificationMode === 'signup' ? 'Account Exists - Verify Email' :
             mode === 'signup' && showVerificationInSignup ? 'Verify Your Email' :
             mode === 'signup' ? 'Create Account' : 
             'Verify Your Email'}
          </h2>
          <button
            className="auth-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          {infoMessage && (
            <div className="auth-info-message">{infoMessage}</div>
          )}
          
          {errors.general && (
            <div className="auth-error-message">{errors.general}</div>
          )}

          {(mode === 'verify' || 
            (mode === 'signup' && showVerificationInSignup) || 
            (mode === 'login' && showVerificationInLogin)) && (
            <div className="auth-verify-info">
              {mode === 'signup' && showVerificationInSignup && pendingVerificationMode === 'signup' ? (
                <>
                  <p>An account with this email already exists!</p>
                  <p>We've resent a verification code to <strong>{formData.email}</strong></p>
                  <p>Please enter the 6-digit code to verify your account:</p>
                </>
              ) : (
                <>
                  <p>We've sent a verification code to <strong>{formData.email}</strong></p>
                  <p>Please enter the 6-digit code below:</p>
                </>
              )}
              {resendCode && (
                <button
                  type="button"
                  className="auth-resend-button"
                  onClick={async () => {
                    try {
                      await resendCode(formData.email);
                      setErrors({ general: 'Verification code sent! Check your email.' });
                    } catch (error) {
                      setErrors({ general: 'Failed to resend code. Please try again.' });
                    }
                  }}
                  disabled={isLoading}
                >
                  Resend Code
                </button>
              )}
            </div>
          )}

          {mode === 'verify' ? (
            <div className="auth-input-group">
              <label htmlFor="verificationCode" className="auth-label">Verification Code</label>
              <input
                type="text"
                id="verificationCode"
                name="verificationCode"
                value={formData.verificationCode}
                onChange={handleInputChange}
                className={`auth-input ${errors.verificationCode ? 'auth-input-error' : ''}`}
                placeholder="Enter 6-digit code"
                maxLength={6}
                pattern="[0-9]{6}"
              />
              {errors.verificationCode && <span className="auth-field-error">{errors.verificationCode}</span>}
            </div>
          ) : (
            <>
              {/* Login form fields */}
              {mode === 'login' && (
                <>
                  <div className="auth-input-group">
                    <label htmlFor="email" className="auth-label">Email</label>
                    <input
                      type="text"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`auth-input ${errors.email ? 'auth-input-error' : ''}`}
                      placeholder="Enter your email"
                      disabled={showVerificationInLogin} // Disable email field when showing verification
                    />
                    {errors.email && <span className="auth-field-error">{errors.email}</span>}
                  </div>

                  {!showVerificationInLogin && (
                    <div className="auth-input-group">
                      <label htmlFor="password" className="auth-label">Password</label>
                      <div className="auth-password-wrapper">
                        <input
                          type={showPassword ? "text" : "password"}
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className={`auth-input ${errors.password ? 'auth-input-error' : ''}`}
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          className="auth-password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                              <line x1="1" y1="1" x2="23" y2="23"/>
                            </svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          )}
                        </button>
                      </div>
                      {errors.password && <span className="auth-field-error">{errors.password}</span>}
                    </div>
                  )}

                  {/* Add verification code field in login mode when needed */}
                  {showVerificationInLogin && (
                    <div className="auth-input-group">
                      <label htmlFor="verificationCode" className="auth-label">Verification Code</label>
                      <input
                        type="text"
                        id="verificationCode"
                        name="verificationCode"
                        value={formData.verificationCode}
                        onChange={handleInputChange}
                        className={`auth-input ${errors.verificationCode ? 'auth-input-error' : ''}`}
                        placeholder="Enter 6-digit code"
                        maxLength={6}
                        pattern="[0-9]{6}"
                      />
                      {errors.verificationCode && <span className="auth-field-error">{errors.verificationCode}</span>}
                    </div>
                  )}
                </>
              )}

              {mode === 'signup' && (
                <>
                  <div className="auth-name-row">
                    <div className="auth-input-group">
                      <label htmlFor="firstName" className="auth-label">First Name</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={`auth-input ${errors.firstName ? 'auth-input-error' : ''}`}
                        placeholder="Enter your first name"
                      />
                      {errors.firstName && <span className="auth-field-error">{errors.firstName}</span>}
                    </div>
                    <div className="auth-input-group">
                      <label htmlFor="lastName" className="auth-label">Last Name</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={`auth-input ${errors.lastName ? 'auth-input-error' : ''}`}
                        placeholder="Enter your last name"
                      />
                      {errors.lastName && <span className="auth-field-error">{errors.lastName}</span>}
                    </div>
                  </div>

                  <div className="auth-input-group">
                    <label htmlFor="email" className="auth-label">Email</label>
                    <input
                      type="text"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`auth-input ${errors.email ? 'auth-input-error' : ''}`}
                      placeholder="Enter your email"
                      disabled={showVerificationInSignup} // Disable email field when showing verification
                    />
                    {errors.email && <span className="auth-field-error">{errors.email}</span>}
                  </div>

                  {!showVerificationInSignup && (
                    <>
                      <div className="auth-input-group">
                        <label htmlFor="password" className="auth-label">Password</label>
                        <div className="auth-password-wrapper">
                          <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            className={`auth-input ${errors.password ? 'auth-input-error' : ''}`}
                            placeholder="Enter your password"
                          />
                          <button
                            type="button"
                            className="auth-password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                <line x1="1" y1="1" x2="23" y2="23"/>
                              </svg>
                            ) : (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                <circle cx="12" cy="12" r="3"/>
                              </svg>
                            )}
                          </button>
                        </div>
                        {errors.password && <span className="auth-field-error">{errors.password}</span>}
                      </div>

                      <div className="auth-input-group">
                        <label htmlFor="confirmPassword" className="auth-label">Confirm Password</label>
                        <div className="auth-password-wrapper">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            className={`auth-input ${errors.confirmPassword ? 'auth-input-error' : ''}`}
                            placeholder="Confirm your password"
                          />
                          <button
                            type="button"
                            className="auth-password-toggle"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                          >
                            {showConfirmPassword ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                <line x1="1" y1="1" x2="23" y2="23"/>
                              </svg>
                            ) : (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                <circle cx="12" cy="12" r="3"/>
                              </svg>
                            )}
                          </button>
                        </div>
                        {errors.confirmPassword && <span className="auth-field-error">{errors.confirmPassword}</span>}
                      </div>
                    </>
                  )}

                  {/* Add verification code field in signup mode when needed */}
                  {showVerificationInSignup && (
                    <div className="auth-input-group">
                      <label htmlFor="verificationCode" className="auth-label">Verification Code</label>
                      <input
                        type="text"
                        id="verificationCode"
                        name="verificationCode"
                        value={formData.verificationCode}
                        onChange={handleInputChange}
                        className={`auth-input ${errors.verificationCode ? 'auth-input-error' : ''}`}
                        placeholder="Enter 6-digit code"
                        maxLength={6}
                        pattern="[0-9]{6}"
                      />
                      {errors.verificationCode && <span className="auth-field-error">{errors.verificationCode}</span>}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          <button
            type="submit"
            className="auth-submit-button"
            disabled={isLoading}
          >
            {isLoading ? 'Please wait...' : (
              mode === 'login' && showVerificationInLogin ? 'Verify Email' :
              mode === 'login' ? 'Sign In' : 
              mode === 'signup' && showVerificationInSignup ? 'Verify Email' :
              mode === 'signup' ? 'Create Account' : 
              'Verify Code'
            )}
          </button>
        </form>

        {mode !== 'verify' && 
         !(mode === 'signup' && showVerificationInSignup) && 
         !(mode === 'login' && showVerificationInLogin) && (
          <>
            <div className="auth-divider">
              <span>or continue with</span>
            </div>

            <div className="auth-social-buttons">
          <button
            type="button"
            className="auth-social-button auth-google-button"
            onClick={() => handleSocialAuth('google')}
            disabled={isLoading}
          >
            <svg className="auth-social-icon" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
        </div>
          </>
        )}

        <div className="auth-switch">
          <p>
            {mode === 'verify' ? (
              <>
                Didn't receive the code? 
                <button
                  type="button"
                  className="auth-switch-button"
                  onClick={switchMode}
                >
                  Go back
                </button>
              </>
            ) : (
              <>
                {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
                <button
                  type="button"
                  className="auth-switch-button"
                  onClick={switchMode}
                >
                  {mode === 'login' ? 'Sign up' : 'Sign in'}
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AuthModal;
